import React from 'react';
import PropTypes from 'prop-types';
import CssBaseline from '@material-ui/core/CssBaseline';
import {
  StylesProvider,
  MuiThemeProvider,
  createTheme,
  createGenerateClassName,
  hexToRgb,
  jssPreset,
  withStyles
} from '@material-ui/core/styles';
import { create } from 'jss';
import jssCompose from 'jss-plugin-compose';
import jssExtend from 'jss-plugin-extend';

import { screens, surfaceShadows, themePalettes } from './variables';
import { getThemeVariant, THEME_VARIANTS, reskinStyles } from './theme-variant';
import overrideStyles from './material-overrides';
import reskinTextColors from './reskin-text-colors';
import reskinTextSizes from './reskin-text-sizes';

const generateClassName = createGenerateClassName();
const createJss = (plugins = []) =>
  create({ plugins: [jssExtend(), ...plugins, ...jssPreset().plugins, jssCompose()] });
const jssByVariant = {
  [THEME_VARIANTS.LEGACY]: createJss(),
  [THEME_VARIANTS.RESKIN]: createJss([reskinTextSizes(), reskinTextColors()])
};

const typographyFontFamilies = {
  [THEME_VARIANTS.LEGACY]: "'Libre Franklin', sans-serif",
  [THEME_VARIANTS.RESKIN]: "'Cabin', sans-serif"
};

const buildTheme = (themeVariant) => {
  const { colorPalette, colors, fonts, onCard, onCardDivider, sizes } = themePalettes[themeVariant];
  const theme = createTheme({
    typography: {
      fontFamily: typographyFontFamilies[themeVariant],
      useNextVariants: true
    },
    palette: {
      primary: {
        main: colorPalette.green_100,
        contrastText: colorPalette.white
      },
      secondary: {
        main: colorPalette.orange_50,
        contrastText: colorPalette.white
      },
      error: {
        main: colors.error
      }
    },
    colors,
    colorPalette,
    onCard,
    onCardDivider,
    fonts,
    screens,
    sizes,
    surfaceShadows,
    spacing: 4,
    themeVariant
  });
  // New-look card tiers, spread through `reskinStyles` so classic cards keep their own styles.
  theme.cardStyles = {
    outlined: {
      backgroundColor: colors.card_background_primary,
      border: `4px solid ${colors.card_border_secondary}`,
      borderRadius: sizes.roundCorners,
      boxShadow: 'none'
    },
    inset: {
      backgroundColor: colorPalette.beige_25,
      border: 'none',
      borderRadius: sizes.innerCorners,
      boxShadow: 'none'
    },
    highlight: {
      backgroundColor: colors.card_background_highlight,
      border: 'none',
      borderRadius: sizes.roundCorners,
      boxShadow: 'none'
    },
    // The body of a card that is already outlined (e.g. inside CollapsibleCard): no second outline.
    body: {
      backgroundColor: 'transparent',
      border: 0,
      boxShadow: 'none'
    }
  };
  // New-look Label: describes something and does nothing (tags, badges, counts, static chips). Outlined with 4px
  // corners so it doesn't read as a pill-shaped button. The rgb() text is the one deliberate mid-green text, which
  // reskin-text-colors otherwise turns to green200.
  theme.labelStyles = {
    outlined: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 4,
      boxSizing: 'border-box',
      width: 'fit-content',
      height: 24,
      padding: '0 8px',
      backgroundColor: 'transparent',
      border: `1px solid ${colorPalette.beige_60}`,
      borderRadius: 4,
      color: hexToRgb(colorPalette.green_75),
      fontSize: 13,
      fontWeight: 500,
      lineHeight: 1,
      whiteSpace: 'nowrap'
    }
  };
  theme.actionStyles = {
    // New-look secondary actions: a translucent beige fill that reads as a soft button on white and beige panels,
    // so only the bark main action stands out.
    secondary: {
      fontWeight: 600,
      backgroundColor: colorPalette.beige_60_op_30,
      border: 0,
      color: colors.text_primary,
      boxShadow: 'none',
      '&:hover': { backgroundColor: colorPalette.beige_60_op_55, boxShadow: 'none' },
      '&:active': { backgroundColor: colorPalette.beige_60_op_80 },
      '&.Mui-disabled': {
        backgroundColor: colorPalette.beige_50,
        color: `${colors.text_primary} !important`,
        opacity: 0.5
      }
    },
    link: {
      color: colors.action_text,
      textDecoration: 'underline',
      textDecorationThickness: '1px',
      '&:hover': { color: colors.action_text, textDecorationThickness: '2px' }
    },
    list: {
      color: colors.action_text,
      borderRadius: 22,
      '&:hover': { color: colors.action_text, backgroundColor: colorPalette.beige_50, textDecoration: 'none' }
    }
  };
  theme.modalTitle = {
    fontSize: 40,
    fontWeight: 300,
    lineHeight: 1,
    color: colorPalette.green_75
  };
  overrideStyles(theme);
  return theme;
};

export const themes = {
  [THEME_VARIANTS.LEGACY]: buildTheme(THEME_VARIANTS.LEGACY),
  [THEME_VARIANTS.RESKIN]: buildTheme(THEME_VARIANTS.RESKIN)
};

// withStyles sits outside the MUI theme, so the global rules use the page-load theme directly.
const pageTheme = themes[getThemeVariant()];
const rootStyles = {
  '@global': {
    '*': {
      fontFamily: pageTheme.fonts.LibreFranklin
    },
    body: {
      backgroundColor: pageTheme.colors.page__background_primary
    },
    ':focus': {
      outline: 'none'
    },
    ...reskinStyles(pageTheme, {
      'button:focus-visible, a:focus-visible, [role=button]:focus-visible, [tabindex]:focus-visible, input:focus-visible, select:focus-visible, textarea:focus-visible':
        {
          outline: `2px solid ${pageTheme.colorPalette.orange_50}`,
          outlineOffset: 2
        },
      // Selects and menu options show focus with the bark field border and the option highlight instead.
      // Roles, not Mui* class names: those get numeric suffixes under a nested ThemeProvider.
      '[aria-haspopup="listbox"]:focus-visible, [role="option"]:focus-visible, [role="menuitem"]:focus-visible': {
        outline: 'none'
      },
      'a:focus-visible': { textDecoration: 'underline' },
      'a:focus-visible > :only-child': {
        outline: `2px solid ${pageTheme.colorPalette.orange_50}`,
        outlineOffset: 2
      }
    }),
    '::-moz-focus-inner': {
      border: 0
    },
    a: {
      // Without this, a link with no class of its own falls back to the browser's default blue (#37788)
      color: pageTheme.palette.secondary.main,
      textDecoration: 'none',
      display: 'contents' // https://drafts.csswg.org/css-display/#valdef-display-contents
    }
  }
};

const ThemeProvider = ({ children, variant = getThemeVariant() }) => (
  <StylesProvider jss={jssByVariant[variant]} generateClassName={generateClassName}>
    <MuiThemeProvider theme={themes[variant]}>
      <CssBaseline />
      {children}
    </MuiThemeProvider>
  </StylesProvider>
);

ThemeProvider.propTypes = {
  children: PropTypes.object.isRequired,
  variant: PropTypes.oneOf(Object.values(THEME_VARIANTS))
};

export default withStyles(rootStyles)(ThemeProvider);
